import { useState, useEffect, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  ArrowLeft,
  XCircle,
  CheckCircle,
  Calendar,
  Trash2,
  Banknote,
  Receipt,
  FileText,
  AlertTriangle,
  RotateCcw,
  Ban,
  Undo2,
} from '@/lib/icons';

import {
  getContractById,
  cancelContract,
  revertDefaultedContract,
  writeOffContract,
  type WriteOffRequest,
  deleteContract,
  activateContract,
  getPaymentSchedule,
  getContractLoan,
  type ContractLoan,
} from '@/features/contracts/api';
import { ContractAgreementActions } from '@/features/contracts/agreement-actions';
import {
  CONTRACT_TABS,
  agreementPdfQueryKey,
  contractDisplayNumber,
  installmentProgressPercent,
  paymentRhythm,
  resolveContractTab,
  unresolvedAttempts,
  type ContractTab,
} from '@/features/contracts/contract';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ApiError, apiErrorMessage } from '@/lib/api';
import { useAppSelector } from '@/store/hooks';
import {
  cn,
  formatCurrency,
  formatCurrencyOrUnavailable,
  truncateId,
} from '@/lib/utils';
import { hasMarkup } from '@/lib/markup';
import { deviceCatalogKeys } from '@/features/device-models/keys';
import {
  CONTRACT_LOAN_PAYMENTS_ENABLED,
  getContractLoanPayments,
  getPayments,
} from '@/features/payments/api';
import { PaymentReceiptSheet } from '@/features/payments/payment-receipt-sheet';
import { ReversePaymentDialog } from '@/features/payments/components/ReversePaymentDialog';
import {
  canReversePayment,
  historyRowStatus,
} from '@/features/payments/reversal';
import {
  depositPaid,
  paymentOriginBadgeClass,
  paymentOriginLabel,
  paymentTypeBadgeClass,
  paymentTypeLabel,
} from '@/features/payments/payment-display';
import { mergePaymentHistory } from '@/features/payments/payment-history';
import { OdooLinkBadge } from '@/features/odoo/components/OdooLinkBadge';
import type { PaymentListItemModel } from '@/features/payments/payment';
import { CopyableId, CopyButton } from '@/components/ui/copyable-id';
import { RecordLink } from '@/components/ui/record-link';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ActionLoadingOverlay, ButtonSpinner } from '@/components/feedback';
import { useAuth } from '@/hooks/useAuth';
import { DeviceLockStatusCard } from '@/features/lock-engine/device-lock-status-card';
import { ContactHistoryPanel } from '@/features/feedback/components/ContactHistoryPanel';
import { DocumentsCard } from '@/features/documents/documents-card';
import { CollectionsTimeline } from '@/features/contracts/components/CollectionsTimeline';
import { RevertDefaultDialog } from '@/features/contracts/components/RevertDefaultDialog';
import { WriteOffDialog } from '@/features/contracts/components/WriteOffDialog';
import { WrittenOffBanner } from '@/features/contracts/components/WrittenOffBanner';
import {
  canRevertDefault,
  canWriteOff,
  estimatedWriteOffAmount,
  isContractWrittenOffError,
} from '@/features/contracts/write-off';
import { CancellationSettlementCard } from '@/features/contracts/components/CancellationSettlementCard';
import { getContractActivity } from '@/features/contracts/api';
import {
  addDays,
  businessToday,
  formatDate,
  formatDateTimeWithZone,
  INSTALLMENT_PERIOD_DAYS,
  parseDateOnly,
  toInstant,
} from '@/lib/dates';
import { DatePickerInput } from '@/components/ui/date-picker-input';

const tabSchema = z.object({
  tab: z.enum(CONTRACT_TABS).optional(),
  /** Payment id to scroll to and flag in Payment History. Set by deep links
   *  from transaction lists (dashboard recent transactions, receipt drawer). */
  highlight: z.string().optional(),
});

export const Route = createFileRoute('/_layout/contracts/$contractId')({
  component: ContractDetailPage,
  validateSearch: tabSchema,
});

function ContractDetailPage() {
  const { contractId } = Route.useParams();
  const search = Route.useSearch();
  const activeTab = resolveContractTab(search);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';
  const cancellationWindowHours =
    useAppSelector((state) => state.tenant.settings?.cancellationWindowHours) ??
    48;

  const { data, isLoading, error } = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => getContractById(contractId),
    // Override the global ``refetchOnWindowFocus: false`` for this page — the
    // escalation level, lock state and activity feed all move from the worker's
    // overdue pass, so a tab left open goes stale without a reload. Applied to
    // every live query below for the same reason.
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  const { data: scheduleData } = useQuery({
    queryKey: ['contract-schedule', contractId],
    queryFn: () => getPaymentSchedule(contractId),
    enabled: !!data?.value,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  const {
    data: paymentsData,
    error: paymentsError,
    isLoading: paymentsLoading,
  } = useQuery({
    queryKey: ['contract-payments', contractId],
    queryFn: () => getPayments({ contractId }),
    enabled: !!data?.value,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  // The other half of the contract's money: loan-service holds the settled
  // installments, including the ZaadCredit auto-deductions, which never reach
  // offerings' own ledger at all. Two queries rather than one so a failure on
  // either side costs the operator only that side (see `loanPaymentsFailed` /
  // `contractPaymentsFailed` below).
  //
  // Gated on CONTRACT_LOAN_PAYMENTS_ENABLED rather than left unconditional so
  // a deployment where offerings-service has not yet shipped
  // `GET /v1/contracts/{id}/payments` can flip it back off without a code
  // change — see that flag's own doc comment in `features/payments/api.ts`.
  const {
    data: loanPaymentsData,
    error: loanPaymentsError,
    isLoading: loanPaymentsLoading,
  } = useQuery({
    queryKey: ['contract-loan-payments', contractId],
    queryFn: () => getContractLoanPayments(contractId),
    enabled: CONTRACT_LOAN_PAYMENTS_ENABLED && !!data?.value,
  });

  const [writeOffDialogOpen, setWriteOffDialogOpen] = useState(false);

  // Loan-service record for this contract — fetched only when the Loan tab is
  // visible to avoid an extra cross-service round-trip on the default view.
  // The write-off dialog also wants it, for the outstanding balance.
  const {
    data: loanData,
    isLoading: loanLoading,
    error: loanError,
  } = useQuery({
    queryKey: ['contract-loan', contractId],
    queryFn: () => getContractLoan(contractId),
    enabled: !!data?.value && (activeTab === 'loan' || writeOffDialogOpen),
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  const {
    data: activityData,
    isLoading: activityLoading,
    isError: activityError,
  } = useQuery({
    queryKey: ['contract-activity', contractId],
    queryFn: () => getContractActivity(contractId),
    enabled: !!data?.value,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });
  const activity = activityData?.value ?? undefined;
  const unresolvedCount = unresolvedAttempts(activity).length;

  const cancelMutation = useMutation({
    mutationFn: () => cancelContract(contractId),
    onSuccess: (result) => {
      if (result.succeeded) {
        queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
        queryClient.invalidateQueries({ queryKey: ['contracts'] });
        // A cancel is not only a contract edit: it releases the handset at the
        // provider and hands the unit back to inventory, and it closes the
        // loan. The screens that report those read their own caches, so
        // without this the unit still reads as financed on the inventory page
        // an operator opens straight after cancelling.
        queryClient.invalidateQueries({ queryKey: deviceCatalogKeys.units() });
        queryClient.invalidateQueries({ queryKey: ['device-unit-activity'] });
        queryClient.invalidateQueries({ queryKey: ['device-actions'] });
        queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
        queryClient.invalidateQueries({ queryKey: ['contract-activity', contractId] });
        // The server's message names an in-flight debit that will settle late.
        toast({
          title: t('common.success'),
          description: result.message?.trim()
            ? result.message
            : t('contracts.cancellation.cancelledToast'),
        });
      }
    },
    // A failed envelope is thrown by handleApiResponse, so it lands here.
    onError: (err) => {
      toast({
        title: t('common.error'),
        description: apiErrorMessage(err, 'Failed to cancel'),
        variant: 'destructive',
      });
    },
  });

  const revertMutation = useMutation({
    mutationFn: (message: string) =>
      revertDefaultedContract(contractId, message),
    onSuccess: () => {
      setRevertDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({
        queryKey: ['contract-activity', contractId],
      });
      toast({
        title: t('common.success'),
        description: t('contracts.revertDefault.revertedToast'),
      });
    },
    onError: (err) => {
      // Written off elsewhere since this page loaded: refresh to drop the action.
      if (isContractWrittenOffError(err)) {
        queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
      }
    },
  });

  const writeOffMutation = useMutation({
    mutationFn: (request: WriteOffRequest) =>
      writeOffContract(contractId, request),
    onSuccess: () => {
      setWriteOffDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({
        queryKey: ['contract-activity', contractId],
      });
      queryClient.invalidateQueries({ queryKey: ['contract-loan', contractId] });
      toast({
        title: t('common.success'),
        description: t('contracts.writeOff.successToast'),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteContract(contractId),
    onSuccess: (result) => {
      if (result.succeeded) {
        queryClient.invalidateQueries({ queryKey: ['contracts'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        toast({
          title: 'Contract Deleted',
          description: 'The contract has been permanently removed.',
        });
        navigate({ to: '/contracts' });
      } else {
        toast({
          title: t('common.error'),
          description: result.errors?.[0] || 'Failed to delete contract',
          variant: 'destructive',
        });
      }
    },
  });

  const [activationSuccess, setActivationSuccess] = useState(false);

  // Marks the contract as activated in the UI and refreshes all dependent
  // queries. Shared between the success path and the error-recovery path
  // (where the backend completed activation but the HTTP response was lost
  // or the user retried after a successful run).
  const finalizeActivation = async () => {
    setActivationSuccess(true);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['contract', contractId] }),
      queryClient.invalidateQueries({
        queryKey: ['contract-schedule', contractId],
      }),
      queryClient.invalidateQueries({ queryKey: ['contracts'] }),
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      // Activation stamps the start date and issues the schedule, both of
      // which the agreement prints — drop the cached PDF so the next
      // download renders the activated document rather than the draft.
      queryClient.removeQueries({ queryKey: agreementPdfQueryKey(contractId) }),
    ]);
  };

  const activateMutation = useMutation({
    mutationFn: () =>
      activateContract(contractId, {
        startDate: activateStartDate,
        endDate: computedEndDate,
        // Upfront (deposit + excess) is now collected pre-activation via
        // the application's pay-deposit / pay-excess / push-payment flow.
        // Activation is paperwork-only — the backend rejects this call
        // if the upfront isn't fully covered yet.
        collectDownPayment: false,
      }),
    onSuccess: async (result) => {
      // handleApiResponse throws on succeeded:false, so this is effectively
      // the success-only branch — the else stays as a defensive fallback.
      if (result.succeeded) {
        await finalizeActivation();
      } else {
        toast({
          title: t('common.error'),
          description: result.errors?.[0] || 'Failed to activate contract',
          variant: 'destructive',
        });
      }
    },
    onError: async (err) => {
      // The backend may have completed activation (SMS sent, status flipped
      // to "active") even when the HTTP response is lost — proxy timeout,
      // ngrok reset, slow worker. It can also return an "already active"
      // error if the user retried after a silent success. In both cases the
      // refetched contract will read as active, and the operator should see
      // success rather than a confusing error.
      try {
        const fresh = await queryClient.fetchQuery({
          queryKey: ['contract', contractId],
          queryFn: () => getContractById(contractId),
        });
        if (fresh.succeeded && fresh.value?.status === 'active') {
          await finalizeActivation();
          return;
        }
      } catch {
        // Refetch failed too — fall through to the real error toast below.
      }

      const apiErr = err as ApiError;
      const description =
        apiErr?.errors?.[0] || apiErr?.message || 'Failed to activate contract';
      toast({
        title: t('common.error'),
        description,
        variant: 'destructive',
      });
    },
  });

  // Auto-close dialog after showing success for 2 seconds
  useEffect(() => {
    if (activationSuccess) {
      const timer = setTimeout(() => {
        setActivateDialogOpen(false);
        setActivationSuccess(false);
        toast({
          title: '🎉 Contract Activated!',
          description: `${data?.value?.customerName ?? 'Customer'}'s financing is now active. The financing period has begun.`,
          duration: 6000,
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [activationSuccess, toast, data?.value?.customerName]);

  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [activateStartDate, setActivateStartDate] = useState(businessToday);

  const contract = data?.value;
  const rhythm = contract ? paymentRhythm(contract) : null;
  const schedule = scheduleData?.value ?? contract?.paymentSchedule ?? [];
  const payments = paymentsData?.value ?? [];
  const loanPayments = loanPaymentsData?.value ?? [];
  // Display-only union. Neither ledger is written to and neither is treated as
  // the other's subset: a payment both services recorded collapses to one row,
  // and everything else stands on its own.
  const historyRows = mergePaymentHistory(payments, loanPayments);

  // `handleApiResponse` throws on a failure envelope, so a query's error is the
  // one signal either way. Named per source because the operator needs to know
  // *which* half of the history is missing, not merely that some is.
  //
  // All three stay gated on the same switch as the query above: on a
  // deployment where CONTRACT_LOAN_PAYMENTS_ENABLED is flipped back off, this
  // panel reverts to exactly the states it had before this feature —
  // today's list, today's empty text — with none of the two-source UI below
  // reachable.
  const contractPaymentsFailed =
    CONTRACT_LOAN_PAYMENTS_ENABLED && !!paymentsError;
  const loanPaymentsFailed =
    CONTRACT_LOAN_PAYMENTS_ENABLED && !!loanPaymentsError;
  const historyIncomplete = contractPaymentsFailed || loanPaymentsFailed;
  const historyLoading =
    CONTRACT_LOAN_PAYMENTS_ENABLED && (paymentsLoading || loanPaymentsLoading);

  // ── Deep-linked payment (?highlight=<paymentId>) ──────────────
  // Arriving from a transaction list, the operator should land on the exact
  // row they clicked rather than hunting through Payment History. Scroll it
  // into view once the payments query resolves, then let the ring fade so it
  // does not read as a permanent selection.
  const highlightPaymentId = search.highlight;
  const highlightRowRef = useRef<HTMLTableRowElement>(null);
  const [highlightVisible, setHighlightVisible] = useState(false);
  const [receiptPayment, setReceiptPayment] =
    useState<PaymentListItemModel | null>(null);
  const [reverseTarget, setReverseTarget] =
    useState<PaymentListItemModel | null>(null);

  useEffect(() => {
    if (!highlightPaymentId || !highlightRowRef.current) return;
    highlightRowRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    setHighlightVisible(true);
    const timer = setTimeout(() => setHighlightVisible(false), 2800);
    return () => clearTimeout(timer);
  }, [highlightPaymentId, historyRows.length]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{t('common.error')}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate({ to: '/contracts' })}
        >
          {t('common.actions.back')}
        </Button>
      </div>
    );
  }

  const statusVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default' as const;
      case 'completed':
        return 'secondary' as const;
      case 'defaulted':
        return 'destructive' as const;
      default:
        return 'outline' as const;
    }
  };

  const scheduleStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">
            Paid
          </Badge>
        );
      case 'partial':
        return (
          <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30">
            Partial
          </Badge>
        );
      case 'overdue':
        return (
          <Badge className="bg-red-500/15 text-red-600 border-red-500/30">
            Overdue
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-gray-500/15 text-gray-600 border-gray-500/30">
            Pending
          </Badge>
        );
      case 'waived':
        return (
          <Badge className="bg-blue-500/15 text-blue-600 border-blue-500/30">
            Waived
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Null when either side of the ratio is unavailable — a bar drawn from a
  // missing numerator is a claim that nothing has been repaid.
  const progressPercent = installmentProgressPercent(
    contract.totalPaid,
    contract.totalAmount
  );

  // Deposit actually banked, read off the ledger rather than assumed from the
  // contract's priced `downPayment` — a contract can sit in pending_payment
  // with the deposit still uncollected, and the two figures must be allowed to
  // disagree. Cent-tolerant compare: the amounts are rounded currency, so an
  // exact `>=` would call a fully-paid deposit short on float drift.
  const depositCollected = depositPaid(payments);
  const depositSettled = depositCollected + 0.005 >= contract.downPayment;

  // An unsigned contract has no window to be outside of — the status check
  // governs it, as on the server.
  const signedAt = toInstant(contract.signedAt);
  const cancellationDeadline = signedAt
    ? signedAt.getTime() + cancellationWindowHours * 60 * 60 * 1000
    : null;
  const withinCancellationWindow =
    cancellationDeadline === null || Date.now() <= cancellationDeadline;

  // Status decides whether the transition is legal, the grant whether this
  // operator may make it, the window whether the server would still accept it.
  const cancellableStatus =
    contract.status === 'active' || contract.status === 'draft';
  const canCancel =
    cancellableStatus &&
    withinCancellationWindow &&
    hasPermission('contracts', 'cancel');
  // Rendered in the button's place: a button that silently vanishes leaves the
  // operator with no reason for why they cannot cancel.
  const cancellationWindowClosed =
    cancellableStatus &&
    !withinCancellationWindow &&
    hasPermission('contracts', 'cancel');
  const showRevertDefault = canRevertDefault(
    contract,
    hasPermission('contracts', 'reinstate')
  );
  const showWriteOff = canWriteOff(contract, hasPermission('contracts', 'writeOff'));
  const canReversePayments = hasPermission('payments', 'reverse');
  const reversalContract = {
    status: contract.status,
    writtenOffAt: contract.writtenOffAt,
  };
  const displayNumber = contractDisplayNumber(contract.id);
  const canActivate =
    (contract.status === 'draft' || contract.status === 'pending_payment') &&
    hasPermission('contracts', 'activate');
  const canDelete =
    contract.status === 'draft' && hasPermission('contracts', 'delete');
  // Loan was rejected during approval (or never reached loan-service) — the
  // contract sits in PENDING_PAYMENT until the agent retries activation.
  const needsLoanRetry = canActivate && !contract.loanId;

  // The backend re-anchors the plan on the start date (installment n falls on
  // start + n × period) and ends the contract on the last installment.
  const computedEndDate = (() => {
    if (!parseDateOnly(activateStartDate)) return '';
    const installments = contract.installmentPlan?.length ?? 0;
    const period = INSTALLMENT_PERIOD_DAYS[contract.installmentFrequency ?? ''];
    if (installments > 0 && period) {
      return addDays(activateStartDate, installments * period);
    }
    if (contract.tenureMonths) {
      return addDays(activateStartDate, contract.tenureMonths * 30);
    }
    return '';
  })();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/contracts' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">{contract.contractNumber}</h1>
          <p className="text-muted-foreground text-sm">
            {contract.customerName}
            {contract.customerPhone
              ? ` (${contract.customerPhone})`
              : ''} - {contract.deviceName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={statusVariant(contract.status)} className="text-sm">
            {t(`contracts.status.${contract.status}`)}
          </Badge>
          {contract.installmentFrequency && (
            <Badge
              className={
                contract.installmentFrequency === 'daily'
                  ? 'bg-blue-500/15 text-blue-600 border-blue-500/30'
                  : contract.installmentFrequency === 'weekly'
                    ? 'bg-violet-500/15 text-violet-600 border-violet-500/30'
                    : 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30'
              }
            >
              {contract.installmentFrequency.charAt(0).toUpperCase() +
                contract.installmentFrequency.slice(1)}
            </Badge>
          )}
          <OdooLinkBadge
            subjectType="contract"
            subjectId={contractId}
            label={t('odoo.badge.invoice')}
            showNotTracked
          />
          <ContractAgreementActions
            contractId={contractId}
            customerName={contract.customerName}
          />
          {canActivate && (
            <Dialog
              open={activateDialogOpen}
              onOpenChange={(open) => {
                // Prevent closing while mutation is in flight or showing success
                if (!open && (activateMutation.isPending || activationSuccess))
                  return;
                setActivateDialogOpen(open);
              }}
            >
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  className={
                    needsLoanRetry
                      ? 'bg-amber-600 hover:bg-amber-700 text-white'
                      : 'bg-primary hover:bg-primary/90 text-primary-foreground'
                  }
                >
                  <CheckCircle className="mr-1 h-3.5 w-3.5" />
                  {needsLoanRetry ? 'Retry Loan & Activate' : 'Activate Contract'}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                {/* ─── Success indicator ─── */}
                {activationSuccess && (
                  <div className="absolute inset-0 z-50 flex flex-col items-center justify-center rounded-lg bg-background/95 backdrop-blur-sm animate-in fade-in duration-300">
                    <CheckCircle className="h-10 w-10 text-emerald-600 dark:text-emerald-400 animate-in zoom-in duration-300" />
                    <p className="mt-3 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                      Contract Activated!
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Down payment collected successfully
                    </p>
                  </div>
                )}

                <DialogHeader>
                  <DialogTitle>Activate Contract</DialogTitle>
                  <DialogDescription>
                    Set the contract dates, confirm down payment collection, and
                    start the financing period for{' '}
                    <span className="font-semibold">
                      {contract.customerName}
                    </span>
                    .
                  </DialogDescription>
                </DialogHeader>

                {/* Financial Summary */}
                <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Device</span>
                    <span className="font-medium">{contract.deviceName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Device Price</span>
                    <span>
                      {formatCurrency(contract.devicePrice, currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Financed Amount
                    </span>
                    <span>
                      {formatCurrency(contract.financedAmount, currency)}
                    </span>
                  </div>
                  {hasMarkup(contract.markupAmount) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Murabaha Markup
                      </span>
                      <span>
                        {formatCurrency(contract.markupAmount, currency)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm border-t pt-1.5">
                    <span className="text-muted-foreground font-medium">
                      Total Contract
                    </span>
                    <span className="font-bold">
                      {formatCurrency(contract.totalAmount, currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-1.5">
                    <span className="text-muted-foreground font-medium">
                      Down Payment (collect now)
                    </span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(contract.downPayment, currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Monthly Payment
                    </span>
                    <span className="font-medium">
                      {formatCurrency(contract.monthlyPayment, currency)} ×{' '}
                      {contract.tenureMonths}mo
                    </span>
                  </div>
                </div>

                {/* Date Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      Start Date
                    </Label>
                    <DatePickerInput
                      value={activateStartDate}
                      onChange={setActivateStartDate}
                      disabled={activateMutation.isPending}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      End Date
                    </Label>
                    <div className="flex h-10 items-center rounded-md border border-dashed bg-muted/30 px-3 text-sm">
                      {formatDate(computedEndDate)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Last installment due date
                    </p>
                  </div>
                </div>

                {/* Activation is paperwork-only — the upfront is collected
                    pre-activation via the application's payment flow. */}
                <p className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
                  The upfront amount is collected from the customer beforehand
                  on the application page. Activation only sets the contract
                  dates and starts the financing schedule.
                </p>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setActivateDialogOpen(false)}
                    disabled={activateMutation.isPending || activationSuccess}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    onClick={() => activateMutation.mutate()}
                    disabled={
                      activateMutation.isPending ||
                      !activateStartDate ||
                      activationSuccess
                    }
                  >
                    {activateMutation.isPending ? (
                      <>
                        <ButtonSpinner className="mr-2" />
                        Activating...
                      </>
                    ) : (
                      'Activate Contract'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {showRevertDefault && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  revertMutation.reset();
                  setRevertDialogOpen(true);
                }}
              >
                <RotateCcw className="mr-1 h-3.5 w-3.5" />
                {t('contracts.revertDefault.action')}
              </Button>
              <RevertDefaultDialog
                open={revertDialogOpen}
                onOpenChange={setRevertDialogOpen}
                contractNumber={contract.contractNumber}
                isPending={revertMutation.isPending}
                error={revertMutation.error}
                onSubmit={(message) => revertMutation.mutate(message)}
              />
            </>
          )}
          {showWriteOff && (
            <>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  writeOffMutation.reset();
                  setWriteOffDialogOpen(true);
                }}
              >
                <Ban className="mr-1 h-3.5 w-3.5" />
                {t('contracts.writeOff.action')}
              </Button>
              <WriteOffDialog
                open={writeOffDialogOpen}
                onOpenChange={setWriteOffDialogOpen}
                contractNumber={displayNumber}
                estimatedAmount={estimatedWriteOffAmount(
                  loanData?.value?.outstandingBalance,
                  contract.remainingBalance
                )}
                currency={currency}
                isPending={writeOffMutation.isPending}
                error={writeOffMutation.error}
                onSubmit={(request) => writeOffMutation.mutate(request)}
              />
            </>
          )}
          {canCancel && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  <XCircle className="mr-1 h-3.5 w-3.5" />
                  Cancel Contract
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <ActionLoadingOverlay
                  loading={cancelMutation.isPending}
                  loadingTitle="Cancelling Contract..."
                  loadingSubtitle="Releasing device back to inventory"
                  tone="destructive"
                  steps={[
                    { label: 'Cancelling contract', status: 'active' },
                    { label: 'Releasing device', status: 'pending' },
                  ]}
                />
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel Contract</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to cancel contract{' '}
                    {contract.contractNumber}? This action cannot be undone. The
                    device will be released back to inventory.
                    {depositCollected > 0 && (
                      <>
                        {' '}
                        The {formatCurrency(depositCollected, currency)} deposit
                        already collected is non-refundable and will not be
                        returned.
                      </>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={cancelMutation.isPending}>
                    Keep Contract
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={(e) => {
                      e.preventDefault();
                      cancelMutation.mutate();
                    }}
                    disabled={cancelMutation.isPending}
                  >
                    {cancelMutation.isPending ? (
                      <>
                        <ButtonSpinner className="mr-2" />
                        Cancelling...
                      </>
                    ) : (
                      'Cancel Contract'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {cancellationWindowClosed && (
            <p className="text-xs text-muted-foreground max-w-[18rem] text-right">
              Cancellation window closed — a contract can only be cancelled
              within {cancellationWindowHours} hours of signing.
            </p>
          )}
          {/* Delete Contract — DRAFT only; a non-draft contract is a
              live financing agreement the backend refuses to delete
              (returns 400). */}
          {canDelete && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <ActionLoadingOverlay
                loading={deleteMutation.isPending}
                loadingTitle="Deleting Contract..."
                loadingSubtitle="Permanently removing contract and releasing device"
                tone="destructive"
                steps={[
                  { label: 'Removing contract data', status: 'active' },
                  { label: 'Releasing device', status: 'pending' },
                  { label: 'Unlocking device', status: 'pending' },
                ]}
              />
              <AlertDialogHeader>
                <AlertDialogTitle className="text-destructive">
                  Delete Contract Permanently
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-2">
                  <span className="block">
                    Are you sure you want to delete contract{' '}
                    <span className="font-semibold">
                      {contract.contractNumber}
                    </span>{' '}
                    for{' '}
                    <span className="font-semibold">
                      {contract.customerName}
                    </span>
                    ?
                  </span>
                  <span className="block text-destructive font-medium">
                    This will permanently remove the contract from the system.
                    The device will be released and unlocked. This action cannot
                    be undone.
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={deleteMutation.isPending}>
                  Keep Contract
                </AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={(e) => {
                    e.preventDefault();
                    deleteMutation.mutate();
                  }}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? (
                    <>
                      <ButtonSpinner className="mr-2" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Permanently'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          )}
        </div>
      </div>

      <WrittenOffBanner
        contract={contract}
        currency={currency}
        odooBadge={
          <OdooLinkBadge
            subjectType="contract"
            subjectId={contractId}
            showNotTracked
          />
        }
      />

      <Tabs
        value={activeTab}
        onValueChange={(v) =>
          navigate({
            to: '.',
            search: (prev: Record<string, unknown>) => {
              const isDefault = v === 'overview' && !prev.highlight;
              return { ...prev, tab: isDefault ? undefined : (v as ContractTab) };
            },
            replace: true,
          })
        }
      >
        <TabsList className="grid w-full grid-cols-6 max-w-3xl">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="payments" className="gap-2">
            <Receipt className="h-4 w-4" />
            {t('payments.title')}
          </TabsTrigger>
          <TabsTrigger value="loan" className="gap-2">
            <Banknote className="h-4 w-4" />
            Loan
          </TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <FileText className="h-4 w-4" />
            {t('documents.card.title')}
          </TabsTrigger>
          <TabsTrigger value="contact">
            {t('feedback.contactHistory.tab')}
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-2">
            Activity
            {unresolvedCount > 0 && (
              <span
                aria-label={`${unresolvedCount} unresolved collection failures`}
                className="inline-flex min-w-5 items-center justify-center rounded-full bg-red-500/15 px-1.5 text-xs font-medium tabular-nums text-red-600 dark:bg-red-500/25 dark:text-red-300"
              >
                {unresolvedCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
      {/* Main layout: 2-column on lg */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: Contract details + tables */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contract Summary */}
          <Card>
            <CardHeader>
              <CardTitle>{t('contracts.detail.summary')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                {/* Customer and Device are both records with a page of their
                    own, so both are links and both look the same. Customer was
                    the odd one out: an agent reading a contract most often
                    wants the person, and had to go back to the customer list
                    and search a name they were already looking at. */}
                <InfoRow
                  label="Customer"
                  value={
                    contract.customerId ? (
                      <RecordLink
                        to="/customers/$customerId"
                        params={{ customerId: contract.customerId }}
                        title={contract.customerName}
                        aria-label={`View customer ${contract.customerName}`}
                      >
                        {contract.customerName}
                      </RecordLink>
                    ) : (
                      contract.customerName
                    )
                  }
                />
                {contract.customerPhone && (
                  <InfoRow
                    label="Phone"
                    // Callable rather than decorative — collections work starts
                    // with dialling this number. Styled quietly so it does not
                    // compete with the two record links beside it: a phone
                    // number is an action, not a place in the portal.
                    value={
                      <a
                        href={`tel:${contract.customerPhone.replace(/[^\d+]/g, '')}`}
                        className="rounded-sm hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                        aria-label={`Call ${contract.customerName} on ${contract.customerPhone}`}
                      >
                        {contract.customerPhone}
                      </a>
                    }
                  />
                )}
                <InfoRow
                  label="Device"
                  value={
                    contract.deviceId ? (
                      <RecordLink
                        to="/inventory/units/$unitId"
                        params={{ unitId: contract.deviceId }}
                        title={contract.deviceName}
                        aria-label={`View device ${contract.deviceName}`}
                      >
                        {contract.deviceName}
                      </RecordLink>
                    ) : (
                      contract.deviceName
                    )
                  }
                />
                <InfoRow label="Plan" value={contract.planName} />
                {contract.branchName && (
                  <InfoRow label="Branch" value={contract.branchName} />
                )}
                <InfoRow
                  label="Tenure"
                  value={`${contract.tenureMonths} months`}
                />
                {contract.installmentFrequency && (
                  <InfoRow
                    label="Frequency"
                    value={
                      contract.installmentFrequency.charAt(0).toUpperCase() +
                      contract.installmentFrequency.slice(1)
                    }
                  />
                )}
                {rhythm && <InfoRow label="Payment Schedule" value={rhythm} />}
                <InfoRow
                  label="Device Price"
                  value={formatCurrency(contract.devicePrice, currency)}
                />
                {contract.creditLimit != null && (
                  <InfoRow
                    label="Credit Limit"
                    value={formatCurrency(contract.creditLimit, currency)}
                  />
                )}
                {contract.financedPrincipal != null && (
                  <InfoRow
                    label="Financed Principal"
                    value={formatCurrency(contract.financedPrincipal, currency)}
                  />
                )}
                {contract.upfrontExcess != null &&
                  contract.upfrontExcess > 0 && (
                    <InfoRow
                      label="Upfront Excess"
                      value={
                        <span className="text-amber-600 dark:text-amber-400">
                          {formatCurrency(contract.upfrontExcess, currency)}
                        </span>
                      }
                    />
                  )}
                <InfoRow
                  label="Down Payment"
                  value={formatCurrency(contract.downPayment, currency)}
                />
                <InfoRow
                  label="Financed Amount"
                  value={formatCurrency(contract.financedAmount, currency)}
                />
                {hasMarkup(contract.markupAmount) && (
                  <InfoRow
                    label="Markup (Murabaha)"
                    value={formatCurrency(contract.markupAmount, currency)}
                  />
                )}
                <InfoRow
                  label="Total Amount"
                  value={
                    <span className="font-bold">
                      {formatCurrency(contract.totalAmount, currency)}
                    </span>
                  }
                />
                <InfoRow
                  label="Monthly Payment"
                  value={
                    <span className="font-bold">
                      {formatCurrency(contract.monthlyPayment, currency)}
                    </span>
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Installment Plan (annotated from backend) */}
          {contract.installmentPlan && contract.installmentPlan.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Installment Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contract.installmentPlan.map((item) => (
                      <TableRow key={item.n}>
                        <TableCell>{item.n}</TableCell>
                        <TableCell>
                          {formatDate(item.due)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.amt, currency)}
                        </TableCell>
                        <TableCell>
                          {item.status === 'paid' ? (
                            <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">
                              Paid
                            </Badge>
                          ) : item.status === 'partial' ? (
                            <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30">
                              Partial
                            </Badge>
                          ) : item.status === 'overdue' ? (
                            <Badge className="bg-red-500/15 text-red-600 border-red-500/30">
                              Overdue
                            </Badge>
                          ) : (
                            <Badge className="bg-gray-500/15 text-gray-600 border-gray-500/30">
                              Upcoming
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.paidAmount != null
                            ? formatCurrency(item.paidAmount, currency)
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Payment Schedule */}
          <Card>
            <CardHeader>
              <CardTitle>{t('contracts.detail.paymentSchedule')}</CardTitle>
            </CardHeader>
            <CardContent>
              {schedule.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Paid At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedule.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          {formatDate(item.dueDate)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.amount, currency)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.paidAmount, currency)}
                        </TableCell>
                        <TableCell>
                          {scheduleStatusBadge(item.status)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.paidAt ? (
                            <span title={formatDateTimeWithZone(item.paidAt)}>
                              {formatDate(item.paidAt)}
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  No payment schedule available
                </div>
              )}
            </CardContent>
          </Card>

        </div>

        {/* Right sidebar */}
        <div className="space-y-6">
          {contract.status === 'cancelled' && contract.cancellation && (
            <CancellationSettlementCard
              cancellation={contract.cancellation}
              currency={currency}
            />
          )}

          {/* Payment Progress */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-semibold">
                  {progressPercent != null ? (
                    `${progressPercent}%`
                  ) : (
                    <span
                      className="text-muted-foreground"
                      title={t('common.notAvailable')}
                    >
                      —
                    </span>
                  )}
                </span>
              </div>
              {/* No bar at all when the figure is unknown. An empty track next
                  to a dash still reads as "nothing repaid yet", which is the
                  claim we cannot make. */}
              {progressPercent != null && (
                <Progress value={progressPercent} className="h-3" />
              )}
              <div className="space-y-2 pt-2">
                {/* Deposit sits above Total Paid because it is collected first
                    and is *not* part of it — Total Paid tracks the installment
                    schedule, which the deposit never enters. Shown whenever the
                    contract prices one, so an uncollected deposit reads as the
                    outstanding item it is rather than going silent. */}
                {contract.downPayment > 0 && (
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-muted-foreground">Deposit Paid</span>
                    <span className="flex items-baseline gap-1.5">
                      <span
                        className={cn(
                          'font-semibold',
                          depositSettled
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-muted-foreground'
                        )}
                      >
                        {formatCurrency(depositCollected, currency)}
                      </span>
                      {!depositSettled && (
                        <span className="text-xs text-muted-foreground">
                          of {formatCurrency(contract.downPayment, currency)}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Paid</span>
                  {/* An earned $0.00 and an underivable figure must not look
                      alike: the first is money we know did not arrive, the
                      second is a number the backend refused to invent. */}
                  <span
                    className={cn(
                      'font-semibold',
                      contract.totalPaid != null
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-muted-foreground'
                    )}
                    title={
                      contract.totalPaid == null
                        ? t('common.notAvailable')
                        : undefined
                    }
                  >
                    {formatCurrencyOrUnavailable(contract.totalPaid, currency)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Remaining</span>
                  <span
                    className={cn(
                      'font-semibold',
                      contract.remainingBalance == null &&
                        'text-muted-foreground'
                    )}
                    title={
                      contract.remainingBalance == null
                        ? t('common.notAvailable')
                        : undefined
                    }
                  >
                    {formatCurrencyOrUnavailable(
                      contract.remainingBalance,
                      currency
                    )}
                  </span>
                </div>
                {contract.overdueAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Overdue</span>
                    <span className="font-semibold text-destructive">
                      {formatCurrency(contract.overdueAmount, currency)}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Device Lock Status — escalation + countdown + thresholds.
              Reads its own snapshot from the lock engine rather than deriving
              one from `contract`: the thresholds are per-frequency tenant
              policy and the overdue walk needs the payment ledger. */}
          <DeviceLockStatusCard contractId={contractId} currency={currency} />

          {/* Dates */}
          <Card>
            <CardHeader>
              <CardTitle>Contract Dates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoRow
                label="Start Date"
                value={formatDate(contract.startDate)}
              />
              <InfoRow
                label="End Date"
                value={formatDate(contract.endDate)}
              />
              <InfoRow
                label="Next Payment"
                value={
                  contract.nextPaymentDate
                    ? formatDate(contract.nextPaymentDate)
                    : '-'
                }
              />
            </CardContent>
          </Card>

        </div>
      </div>
        </TabsContent>

        <TabsContent value="payments">
          {/* Payment History */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Payment History</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate({ to: '/payments' })}
                >
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {historyRows.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('payments.columns.amount')}</TableHead>
                      {/* Without this the deposit is indistinguishable from an
                          installment, and the ledger reads as if the customer
                          had started repaying before servicing began. The same
                          cell carries the origin, which is what separates money
                          an agent took from money the mandate debited. */}
                      <TableHead>{t('payments.columns.type')}</TableHead>
                      <TableHead>{t('payments.columns.method')}</TableHead>
                      <TableHead>{t('payments.columns.reference')}</TableHead>
                      <TableHead>{t('payments.columns.date')}</TableHead>
                      <TableHead className="w-10">
                        <span className="sr-only">
                          {t('payments.receipt.title')}
                        </span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historyRows.map((row) => {
                      const isHighlighted = row.id === highlightPaymentId;
                      const status = historyRowStatus(row);
                      // A settlement that did not complete is the row worth
                      // acting on, so it says so; completed rows stay quiet.
                      const settled =
                        status === 'completed' || status === 'pending';
                      // The kind of money when we know it, and the origin when
                      // it adds something: on a loan-only row it is all we have,
                      // and an automatic deduction is worth naming even beside a
                      // type, because no agent and no counter was involved.
                      const showOrigin =
                        row.origin !== null &&
                        (row.type === null || row.origin === 'auto_deduction');
                      // Bound once: a const keeps the null check narrowing the
                      // value inside the click handler below.
                      const receipt = row.payment;
                      const noReference =
                        !row.providerTransactionId && !row.issuerTransactionId;
                      const typeBadge = row.type !== null && (
                        <Badge
                          className={cn(
                            'whitespace-nowrap text-[10px]',
                            paymentTypeBadgeClass(row.type)
                          )}
                        >
                          {paymentTypeLabel(t, row.type)}
                        </Badge>
                      );
                      return (
                      <TableRow
                        key={`${row.source}:${row.id}`}
                        ref={isHighlighted ? highlightRowRef : undefined}
                        className={cn(
                          'group transition-all duration-500',
                          isHighlighted &&
                            highlightVisible &&
                            'bg-primary/10 ring-2 ring-inset ring-primary/60'
                        )}
                      >
                        <TableCell>
                          <span
                            className={cn(
                              'font-semibold',
                              settled
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-muted-foreground line-through'
                            )}
                          >
                            {formatCurrency(row.amount, currency)}
                          </span>
                          {status !== 'completed' && (
                            <Badge
                              variant="outline"
                              className="ml-2 align-middle text-[10px]"
                            >
                              {t(`payments.status.${status}`)}
                            </Badge>
                          )}
                          {row.source !== 'loan' && (
                            <OdooLinkBadge
                              subjectType="payment"
                              subjectId={row.id}
                              className="ml-2 align-middle"
                            />
                          )}
                          {row.failureReason && (
                            <p
                              title={t('payments.receipt.failureReason')}
                              className="mt-1 max-w-[28ch] text-xs font-normal text-muted-foreground"
                            >
                              {row.failureReason}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* A row with only a type renders exactly the markup
                              it always did; the wrapper appears only when there
                              is a second pill to lay out beside it. */}
                          {showOrigin && row.origin !== null ? (
                            <span className="flex flex-wrap items-center gap-1">
                              {typeBadge}
                              <Badge
                                className={cn(
                                  'whitespace-nowrap text-[10px]',
                                  paymentOriginBadgeClass(row.origin)
                                )}
                              >
                                {paymentOriginLabel(t, row.origin)}
                              </Badge>
                            </span>
                          ) : (
                            typeBadge
                          )}
                        </TableCell>
                        <TableCell>
                          {row.method ? (
                            <Badge variant="outline" className="text-xs">
                              {t(`payments.method.${row.method}`, {
                                defaultValue: row.method,
                              })}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {row.providerTransactionId ? (
                            <span className="group/id inline-flex items-center gap-1.5">
                              <span title={row.providerTransactionId}>
                                {truncateId(row.providerTransactionId, 12, 6)}
                              </span>
                              <CopyButton
                                value={row.providerTransactionId}
                                label={t('payments.receipt.copyReference')}
                                copiedLabel={t('common.copied')}
                              />
                            </span>
                          ) : (
                            noReference && '—'
                          )}
                          {/* The wallet issuer's own id for the same money —
                              distinct from the aggregator id above, and only
                              ever known on an offerings-backed row. */}
                          {row.issuerTransactionId && (
                            <span className="mt-1 flex items-center gap-1.5">
                              <span className="text-[9px] font-sans uppercase tracking-wide text-muted-foreground/70">
                                {t('payments.columns.issuerReference')}
                              </span>
                              <CopyableId
                                value={row.issuerTransactionId}
                                head={12}
                                tail={6}
                                copyLabel={t('payments.receipt.copyIssuerReference')}
                                copiedLabel={t('common.copied')}
                              />
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          <span title={formatDateTimeWithZone(row.occurredAt)}>
                            {formatDate(row.occurredAt)}
                          </span>
                        </TableCell>
                        <TableCell className="w-10 whitespace-nowrap p-0 pr-3 text-right">
                          {receipt &&
                            canReversePayment(
                              receipt,
                              canReversePayments,
                              reversalContract
                            ) && (
                              <button
                                type="button"
                                onClick={() => setReverseTarget(receipt)}
                                aria-label={t('payments.reversal.actionFor', {
                                  amount: formatCurrency(row.amount, currency),
                                })}
                                title={t('payments.reversal.action')}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-all duration-150 hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background md:opacity-0 md:group-hover:opacity-100"
                              >
                                <Undo2 className="h-4 w-4" />
                              </button>
                            )}
                          {/* Receipts are an offerings artefact: the drawer
                              fetches `/v1/payments/{id}`, which knows nothing
                              about a loan-service payment id. A row only
                              loan-service has therefore offers no receipt —
                              deliberately, not by oversight. */}
                          {receipt && (
                            <button
                              type="button"
                              onClick={() => setReceiptPayment(receipt)}
                              aria-label={t('payments.receipt.openFor', {
                                customer:
                                  receipt.customerName ||
                                  t('payments.unknownCustomer'),
                                amount: formatCurrency(row.amount, currency),
                              })}
                              title={t('payments.receipt.title')}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-all duration-150 hover:bg-accent hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background md:opacity-0 md:group-hover:opacity-100"
                            >
                              <Receipt className="h-4 w-4" />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : historyLoading ? (
                // An empty card while a source is still answering would read as
                // "nothing was paid", which is the claim this panel gets wrong.
                <div className="space-y-2 py-2">
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                </div>
              ) : (
                !historyIncomplete && (
                  <div className="py-8 text-center text-muted-foreground">
                    No payments recorded yet
                  </div>
                )
              )}

              {/* A ledger that lost one of its two sources is still worth
                  showing, but "no payments" and "we could not read the
                  payments" are different sentences and an agent handling an
                  "I paid and it is not showing" call needs the right one. */}
              {historyIncomplete && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="space-y-1">
                    {loanPaymentsFailed && (
                      <span className="block">
                        {t('payments.partial.installments')}
                      </span>
                    )}
                    {contractPaymentsFailed && (
                      <span className="block">
                        {t('payments.partial.contractPayments')}
                      </span>
                    )}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="loan">
          <LoanPanel
            isLoading={loanLoading}
            error={loanError}
            loan={loanData?.value ?? null}
            errors={loanData?.errors}
            currency={currency}
          />
        </TabsContent>

        {/* The signed agreement scan and anything else filed against this
            contract. The generated agreement PDF on the Overview tab is a
            different artefact and keeps its own download button. */}
        <TabsContent value="documents">
          <DocumentsCard
            entityType="offerings.contract"
            entityId={contractId}
            subjectUserId={contract.customerUserId}
          />
        </TabsContent>

        {/* An agent chasing an overdue contract can see that the customer has
            been complaining without leaving the contract — which is the whole
            reason the union exists. */}
        <TabsContent value="contact">
          <ContactHistoryPanel contractId={contractId} />
        </TabsContent>

        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Activity</CardTitle>
              <CardDescription>
                Contract lifecycle, payments and every automatic collection
                attempt, newest first.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CollectionsTimeline
                events={activity}
                isLoading={activityLoading}
                isError={activityError}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Receipt drawer for the Payment History rows. Kept mounted so the
          close animation can play out; the detail query is gated on `open`. */}
      <PaymentReceiptSheet
        paymentId={receiptPayment?.id ?? null}
        open={!!receiptPayment}
        onOpenChange={(open) => {
          if (!open) setReceiptPayment(null);
        }}
        fallbackCustomerName={
          receiptPayment?.customerName || contract?.customerName
        }
        fallbackContractId={contractId}
        contract={reversalContract}
      />
      {reverseTarget && (
        <ReversePaymentDialog
          open
          onOpenChange={(open) => {
            if (!open) setReverseTarget(null);
          }}
          paymentId={reverseTarget.id}
          fallback={reverseTarget}
          contractId={contractId}
          currency={currency}
        />
      )}
    </div>
  );
}

/**
 * One label/value pair in the summary grid.
 *
 * The label never shrinks and the value never overflows: a long value (a
 * three-part customer name, now carrying a link arrow as well) would otherwise
 * squeeze the label until it wrapped mid-word, and the two columns beside each
 * other would stop lining up. `min-w-0` is what lets the value's own `truncate`
 * take effect inside a flex row.
 *
 * Because truncation clips, a plain text value also carries its full text as a
 * `title` — an ellipsised provider id or exchange rate has to stay readable,
 * not just tidy. Element values set their own (see the record links above).
 */
function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  const plainText =
    typeof value === 'string' || typeof value === 'number'
      ? String(value)
      : undefined;
  return (
    <div className="flex justify-between items-center gap-4 py-1 border-b border-border/50 last:border-0">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <span
        title={plainText}
        className="min-w-0 truncate text-right text-sm font-medium"
      >
        {value}
      </span>
    </div>
  );
}

const loanStatusBadge = (status: string) => {
  const map: Record<string, string> = {
    pending:
      'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
    approved:
      'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
    active:
      'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
    completed:
      'bg-slate-500/15 text-slate-600 border-slate-500/30 dark:text-slate-400',
    rejected: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
    cancelled:
      'bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400',
    defaulted:
      'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
  };
  return (
    <Badge className={map[status] ?? ''}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

function LoanPanel({
  isLoading,
  error,
  loan,
  errors,
  currency,
}: Readonly<{
  isLoading: boolean;
  error: unknown;
  loan: ContractLoan | null;
  errors?: string[];
  currency: string;
}>) {
  if (isLoading) {
    return (
      <div className="grid gap-6 lg:grid-cols-3">
        <Skeleton className="lg:col-span-2 h-72" />
        <Skeleton className="h-72" />
      </div>
    );
  }

  // Network/transport failure (query threw) or backend returned succeeded:false
  // (e.g. loan-service unreachable) — surface a hint rather than a blank panel.
  if (error || (errors && errors.length > 0)) {
    const message =
      (error instanceof Error && error.message) ||
      errors?.[0] ||
      'Failed to load loan details from loan-service.';
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <Banknote className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
          <p className="font-medium text-destructive">Unable to load loan</p>
          <p className="mt-1 text-sm text-muted-foreground">{message}</p>
        </CardContent>
      </Card>
    );
  }

  if (!loan) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <Banknote className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
          <p className="font-medium">No loan linked yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            A loan record is created when this contract is activated. Activate
            the contract to generate one.
          </p>
        </CardContent>
      </Card>
    );
  }

  const markupRatePct = (loan.markupRate * 100).toFixed(2);

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Banknote className="h-5 w-5" />
              Loan #{loan.id}
            </CardTitle>
            {loanStatusBadge(loan.status)}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
              <InfoRow
                label="MSISDN"
                value={<span className="font-mono">{loan.msisdn}</span>}
              />
              <InfoRow label="Purpose" value={loan.purpose} />
              <InfoRow
                label="Principal"
                value={formatCurrency(loan.principal, currency)}
              />
              {hasMarkup(loan.markup) && (
                <InfoRow
                  label="Markup"
                  value={`${formatCurrency(loan.markup, currency)} (${markupRatePct}%)`}
                />
              )}
              <InfoRow
                label="Total Amount"
                value={
                  <span className="font-semibold">
                    {formatCurrency(loan.totalAmount, currency)}
                  </span>
                }
              />
              <InfoRow label="Term" value={`${loan.termDays} days`} />
              {loan.installmentFrequency && (
                <InfoRow
                  label="Frequency"
                  value={
                    loan.installmentFrequency.charAt(0).toUpperCase() +
                    loan.installmentFrequency.slice(1)
                  }
                />
              )}
              {loan.providerId && (
                <InfoRow label="Provider" value={loan.providerId} />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Outstanding</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-3xl font-bold">
              {formatCurrency(loan.outstandingBalance, currency)}
            </div>
            <p className="text-sm text-muted-foreground">
              Remaining balance reported by loan-service.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Disbursement</CardTitle>
          </CardHeader>
          <CardContent>
            {loan.disbursementStatus ? (
              <Badge
                className={
                  loan.disbursementStatus === 'completed'
                    ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30'
                    : loan.disbursementStatus === 'failed'
                      ? 'bg-red-500/15 text-red-600 border-red-500/30'
                      : 'bg-amber-500/15 text-amber-600 border-amber-500/30'
                }
              >
                {loan.disbursementStatus.charAt(0).toUpperCase() +
                  loan.disbursementStatus.slice(1)}
              </Badge>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Dates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow
              label="Created"
              value={formatDate(loan.createdAt)}
            />
            <InfoRow
              label="Due"
              value={
                loan.dueDate
                  ? formatDate(loan.dueDate)
                  : '-'
              }
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
